import type { FeedMediaCaptionTrack, FeedMediaPlayback, FeedMediaVariant } from '../../types/feedContract'
import type { ResolvedCaptionTrack } from '../../hooks/useVideoCaptions'

const HLS_MANIFEST_SUFFIX_PATTERN = /\/[^/]*\.m3u8.*$/
const URL_QUERY_OR_FRAGMENT_PATTERN = /[?#]/

export interface MediaItem {
  id?: string
  type: string
  url: string
  playback?: FeedMediaPlayback
  variants?: FeedMediaVariant[] | null
  width?: number | null
  height?: number | null
}

export function buildImageSrcSet(item: MediaItem): string | undefined {
  const variants = (item.variants ?? []).filter((v) => v?.url && Number.isFinite(v.width) && v.width > 0)
  if (variants.length === 0) return undefined
  const entries = variants.map((v) => `${v.url} ${v.width}w`)
  const originalWidth = item.width
  if (originalWidth && originalWidth > variants[variants.length - 1].width) entries.push(`${item.url} ${originalWidth}w`)
  return entries.join(', ')
}

export const IMAGE_SIZES_ATTR = '(max-width: 560px) 100vw, 520px'

export function isHlsUrl(url: string): boolean {
  return url?.endsWith('.m3u8') || url?.includes('.m3u8')
}

export function resolveVideoMedia(item: MediaItem) {
  const playback: FeedMediaPlayback = item.playback || {}
  const sourceUrl = firstString(item.url) || ''
  const hlsManifestUrl = firstString(
    playback.hls_url,
    isHlsUrl(sourceUrl) ? sourceUrl : undefined,
    sourceUrl.includes('/fallback.mp4') ? getSiblingHlsAsset(sourceUrl, 'manifest.m3u8') : undefined,
  )
  const fallbackUrl = firstString(
    playback.fallback_url,
    !isHlsUrl(sourceUrl) ? sourceUrl : undefined,
    hlsManifestUrl ? getSiblingHlsAsset(hlsManifestUrl, 'fallback.mp4') : undefined,
  )
  const posterUrl = firstString(
    playback.poster_url,
    hlsManifestUrl ? getPosterFromHls(hlsManifestUrl) : undefined,
  )

  return {
    src: hlsManifestUrl || fallbackUrl || sourceUrl,
    fallbackSrc: fallbackUrl,
    poster: posterUrl,
    isHls: Boolean(hlsManifestUrl),
    captions: resolveCaptionTracks(playback.captions),
  }
}

/**
 * Index of the first media item `CardMedia` renders as a video player (the
 * `index` in its `${postId}-${index}` id), or -1 when the post has none.
 * Surfaces that nominate a card's video for playback use it rather than
 * assuming the video comes first.
 */
export function firstVideoMediaIndex(media: readonly MediaItem[] | null | undefined): number {
  return (media ?? []).findIndex(
    (item) => (item.type === 'video' || isHlsUrl(item.url)) && Boolean(resolveVideoMedia(item).src),
  )
}

/**
 * Keeps well-formed sidecar caption tracks: an absolute http(s) url, a
 * trimmed, lower-cased, non-empty lang, a label defaulting to the lang, and
 * `subtitles` unless the kind is `captions`. Reads the same documented keys
 * (url / lang / label / kind) as the Go `feedcontract.NormalizeCaptionTracks`,
 * which has already run on every feed payload; this is a defensive re-check
 * with the same rules (the kind match folds case).
 */
export function resolveCaptionTracks(
  captions: FeedMediaCaptionTrack[] | null | undefined,
): ResolvedCaptionTrack[] {
  if (!Array.isArray(captions)) return []
  const resolved: ResolvedCaptionTrack[] = []
  for (const track of captions) {
    if (!track || typeof track !== 'object') continue
    const url = firstString(track.url)
    if (!url || !isHttpUrl(url)) continue
    const lang = (firstString(track.lang) || '').toLowerCase()
    if (!lang) continue
    resolved.push({
      url,
      lang,
      label: firstString(track.label) || lang,
      kind: String(track.kind ?? '').toLowerCase() === 'captions' ? 'captions' : 'subtitles',
    })
  }
  return resolved
}

function isHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value)
    return (parsed.protocol === 'https:' || parsed.protocol === 'http:') && Boolean(parsed.host)
  } catch {
    return false
  }
}

export function getPosterFromHls(url: string): string | undefined {
  if (!isHlsUrl(url)) return undefined
  return url.replace(HLS_MANIFEST_SUFFIX_PATTERN, '/poster_0000000000.jpeg')
}

export function getVideoSrcWithPreview(src: string): string {
  if (!isHlsUrl(src) && !src.includes('#t=')) {
    return `${src}#t=0.001`
  }
  return src
}

function getSiblingHlsAsset(url: string, filename: string): string | undefined {
  if (!url) return undefined
  const base = url.split(URL_QUERY_OR_FRAGMENT_PATTERN)[0]
  const slashIndex = base.lastIndexOf('/')
  if (slashIndex < 0) return undefined
  return `${base.slice(0, slashIndex + 1)}${filename}`
}

function firstString(...values: Array<unknown>): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return undefined
}
